import { useTitleHook } from 'hooks/page-title.hook';
import { ReactNode } from 'react';
import { PageHeader } from 'shared/ui/details-page-header';
import { ErrorBanner } from 'shared/ui/error-banner';
import { LoadingBanner } from 'shared/ui/loading-banner';
import { useIdParam } from 'utils/url-paramters.hook';
import { GridLayout } from '../../shared/ui/grid-layout';
import { CreateSalesDepartmentDto, SalesDepartmentDto, UpdateSalesDepartmentDto, UserDto } from '@sr/dto';
import { TableRow, TableCell } from '@mui/material';
import { Stack, Typography, Box, IconButton } from '@mui/material';
import { useSalesDepartment, useUpdateSalesDepartment } from '../../entities/sales-departments/sales-departments.hooks';
import PersonIcon from '@mui/icons-material/Person';
import PercentIcon from '@mui/icons-material/Percent';
import CurrencyRubleIcon from '@mui/icons-material/CurrencyRuble';
import EditIcon from '@mui/icons-material/Edit';
import { useCreateOrEditDialog } from '../../hooks/create-or-edit-dialog.hook';
import { formTools, PopupFormData } from '../../entities/sales-departments/sales-departments-create-edit.form';
import { SalesDepartmentDialog } from '../../entities/sales-departments/sales-department-dialog';
import { NotificationsConfig } from '../../shared/ui/snack';

type RowProps = {
  item: UserDto;
  managerId: number | null;
};
type DetailItemProps = {
  icon: ReactNode;
  name: string;
  value: string | ReactNode;
};
const notificationsConfig: NotificationsConfig = {
  itemTitleForm1: 'отдел продаж',
  itemTitleForm2: 'отдела продаж',
  gender: 'male',
};

export const DetailItem = ({ icon, name, value }: DetailItemProps) => {
  return (
    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: '6px' }} component="div">
      {icon}
      {name} - <b>{value}</b>
    </Typography>
  );
};
export const Row = ({ item, managerId }: RowProps) => {
  return (
    <TableRow key={item.id}>
      <TableCell>{item.id}</TableCell>
      <TableCell align="left">
        {item.firstName} {item.secondName}
      </TableCell>
      <TableCell>{item.id === managerId ? 'Руководитель отдела' : 'Подчиненный'}</TableCell>
    </TableRow>
  );
};
const SalesDepartmentDetailsPage = () => {
  const id = useIdParam('id');

  const { item, error, isLoading } = useSalesDepartment(id);
  const updateItem = useUpdateSalesDepartment();
  const { dialogProps, openToEdit, itemToEdit } = useCreateOrEditDialog<SalesDepartmentDto, CreateSalesDepartmentDto, UpdateSalesDepartmentDto, PopupFormData>({
    addHandler: async (dto) => {},
    updateHandler: async (dto) => {
      await updateItem(dto);
    },
    formTools: formTools,
    notificationsConfig,
  });
  const columns = ['ID пользователя', 'Имя пользователя', 'Роль в отделе'];
  useTitleHook(item?.title && `Отдел продаж ${item?.title}`);

  if (!item)
    if (error) return <ErrorBanner errorMessage={error} />;
    else return <LoadingBanner />;

  return (
    <>
      <PageHeader
        title={
          <PageHeader.Text>
            <PageHeader.Text.Stressed>{`Отдел продаж ${item?.title}`}</PageHeader.Text.Stressed>
            <IconButton
              onClick={() => {
                openToEdit(item);
              }}
            >
              <EditIcon />
            </IconButton>
          </PageHeader.Text>
        }
        subHeader={
          <Stack spacing={1}>
            <DetailItem icon={<PersonIcon />} value={item.managerId ? `${item.manager.firstName} ${item.manager.secondName}` : 'Не назначен'} name={'Руководитель отдела'} />
            <DetailItem
              icon={<PercentIcon />}
              value={item.planFulfillmentPercentage ? `${item.planFulfillmentPercentage}%` : 'Не определен'}
              name={'Продаж при исполнении плана '}
            />
            <DetailItem
              icon={<PercentIcon />}
              value={item.planNonFulfillmentPercentage ? `${item.planNonFulfillmentPercentage}%` : 'Не определен'}
              name={'Продаж при не исполнении плана '}
            />
            <DetailItem
              icon={<CurrencyRubleIcon />}
              value={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {item.plan || 'Не определен'}
                  {item.plan ? <CurrencyRubleIcon sx={{ height: '16px', width: '16px' }} /> : null}
                </Box>
              }
              name={'План продаж'}
            />
          </Stack>
        }
      />
      <SalesDepartmentDialog dialogProps={dialogProps} itemToEdit={itemToEdit} />
      <GridLayout
        columns={columns}
        items={item.employees}
        isLoading={isLoading}
        error={error}
        totalItemsCount={0}
        noItemsText="Список пользователей пуст"
        itemRender={(i) => <Row key={i.id} item={i} managerId={item.managerId} />}
      />
    </>
  );
};

export default SalesDepartmentDetailsPage;
